import { FRAMEWORK_CONFIGS, type Framework } from '~/lib/stores/techStackSelection';
import type { AssistantMessageBuilder } from './getTemplates';
import { PTK, type PTKValue } from './constants';

interface ExamplePrompt {
  id: string;
  titleKey: string;
  textKey: string;
  titleDefault: string;
  textDefault: string;
  icon: string;
  image: string;
  repoUrl: string;
  assistantMessageBuilder?: AssistantMessageBuilder;
  chatComment?: string;
  framework: Framework;
  category?: string;
  aboutTemplate: string;
  isPaid: boolean;
  price: number;
  previewLink: string;
  screenshotLinks: string[];
  featuresList: string[];
  templateKey: PTKValue;
}

export type { ExamplePrompt };

export const examplePrompts: ExamplePrompt[] = [
  {
    id: '1',
    titleKey: 'chat.examplePrompts.prompt1.title',
    textKey: 'chat.examplePrompts.prompt1.text',
    titleDefault: 'SOHO: Premium eCommerce Website',
    textDefault:
      'A high-end furniture e-commerce website featuring a polished design, seamless browsing experience, and fully integrated payment system.',
    icon: 'landingPageIcon',
    image: 'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/new-templates/thumbnails/soho.avif',
    repoUrl: 'https://github.com/dualiteindia/Interior-Design-Template-Dualite',
    framework: 'react_tailwind',
    category: 'website',
    aboutTemplate:
      'SOHO is a premium furniture e-commerce template designed for modern interior brands. It features a stunning hero section, product catalog with filtering, a fully functional shopping cart, and checkout flow. Perfect for launching a furniture or home décor store in minutes.',
    isPaid: false,
    price: 0,
    previewLink: 'https://soho.dualite.app/',
    screenshotLinks: [
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/SOHO-Premium-eCommerce-Website/banner.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/SOHO-Premium-eCommerce-Website/01.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/SOHO-Premium-eCommerce-Website/02.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/SOHO-Premium-eCommerce-Website/03.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/SOHO-Premium-eCommerce-Website/04.avif',
    ],
    featuresList: [
      'Premium hero section with animated transitions',
      'Product catalog with category filtering & search',
      'Fully functional shopping cart & checkout flow',
      'Responsive design optimized for all devices',
      'Integrated payment system ready for customization',
      'SEO-friendly markup & meta tags',
    ],
    templateKey: PTK.SOHO,
  },
  {
    id: '2',
    titleKey: 'chat.examplePrompts.prompt2.title',
    textKey: 'chat.examplePrompts.prompt2.text',
    titleDefault: 'Converge: AI SaaS Landing Page',
    textDefault:
      'Connect your favorite apps, set triggers and watch AI handle the rest. Automate workflows with intelligent AI agents',
    icon: 'mobileAppIcon',
    image: 'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/new-templates/thumbnails/converge.avif',
    repoUrl: 'https://github.com/dualiteindia/converge-ai-template-dualite',
    framework: 'react_tailwind',
    category: 'website',
    aboutTemplate:
      'Converge AI is a modern SaaS landing page template for AI automation platforms. Showcase your AI-powered workflow tools with sleek animations, feature grids, and a compelling call-to-action. Ideal for startups building automation or no-code AI tools.',
    isPaid: false,
    price: 0,
    previewLink: 'https://converge-ai.dualite.app/',
    screenshotLinks: [
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/converge-ai/49.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/converge-ai/50.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/converge-ai/51.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/converge-ai/52.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/converge-ai/53.avif',
    ],
    featuresList: [
      'Modern SaaS landing page with gradient accents',
      'Animated feature cards & integration showcase',
      'Pricing table with toggle for monthly/yearly',
      'Testimonial carousel with social proof',
      'CTA sections with email capture forms',
      'Dark mode design out of the box',
    ],
    templateKey: PTK.CONVERGEAI,
  },
  {
    id: '3',
    titleKey: 'chat.examplePrompts.prompt3.title',
    textKey: 'chat.examplePrompts.prompt3.text',
    titleDefault: 'Soothemi - Yoga & Wellness',
    textDefault: 'Find Your Inner Balance with Soothemi. Yoga and wellness for a calm mind.',
    icon: 'landingPageIcon',
    image: 'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/new-templates/thumbnails/soothemi.avif',
    repoUrl: 'https://github.com/dualiteindia/Soothemi',
    framework: 'react_tailwind',
    category: 'website',
    aboutTemplate:
      'Soothemi is a calming, wellness-focused website template for yoga studios, meditation centers, and holistic health brands. With serene color palettes, fluid animations, and mindful layouts, it creates a tranquil digital experience for your visitors.',
    isPaid: false,
    price: 0,
    previewLink: 'https://soothemi.dualite.app/',
    screenshotLinks: [
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/soothemi-yoga-wellness/80.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/soothemi-yoga-wellness/5.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/soothemi-yoga-wellness/6.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/soothemi-yoga-wellness/7.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/soothemi-yoga-wellness/8.avif',
    ],
    featuresList: [
      'Serene hero section with calming animations',
      'Class schedule & booking section',
      'Instructor profiles with bios',
      'Testimonials from happy clients',
      'Contact form & location map integration',
      'Soft, wellness-inspired color palette',
    ],
    templateKey: PTK.SOOTHEMI,
  },
  {
    id: '4',
    titleKey: 'chat.examplePrompts.prompt4.title',
    textKey: 'chat.examplePrompts.prompt4.text',
    titleDefault: 'Horai Restaurant Website',
    textDefault:
      'Horai is a sleek, high-performance restaurant template built for modern dining brands. With a visually striking layout, fluid interactions, and a polished aesthetic, Horai delivers an immersive digital experience.',
    icon: 'landingPageIcon',
    image: 'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/new-templates/thumbnails/horai.avif',
    repoUrl: 'https://github.com/dualiteindia/Horai',
    framework: 'react_tailwind',
    category: 'website',
    aboutTemplate:
      'Horai is a stunning restaurant website template with scroll-based animations, a beautiful menu showcase, and reservation system. Built for modern dining brands, cafés, and food businesses that want a premium online presence.',
    isPaid: false,
    price: 0,
    previewLink: 'https://horai.dualite.app/',
    screenshotLinks: [
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/horai/81.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/horai/13.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/horai/14.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/horai/15.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/horai/16.avif',
    ],
    featuresList: [
      'Immersive hero with parallax scroll effects',
      'Interactive menu showcase with categories',
      'Photo gallery with lightbox viewer',
      'Online reservation / booking form',
      'Google Maps integration for location',
      'Fully responsive & mobile-first design',
    ],
    templateKey: PTK.HORAI,
  },
  {
    id: '5',
    titleKey: 'chat.examplePrompts.prompt5.title',
    textKey: 'chat.examplePrompts.prompt5.text',
    titleDefault: 'AI Selfie Video Trend',
    textDefault: 'Enter a selfie URL, pick two movies, and let AI transport you seamlessly between sets.',
    icon: 'mobileAppIcon',
    image: 'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/new-templates/thumbnails/selfie.avif',
    repoUrl: 'https://github.com/dualiteindia/ai-selfie',
    framework: 'react_tailwind',
    category: 'ai',
    aboutTemplate:
      'AI Selfie Video Trend is a fun, viral-ready AI app template. Users upload a selfie, select movie scenes, and the AI generates a video of them transitioning between iconic sets. Great for social media apps and AI-powered creative tools.',
    isPaid: false,
    price: 0,
    previewLink: 'https://video-generator.dualite.app/',
    screenshotLinks: [
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/ai-selfie-video-trend/58.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/ai-selfie-video-trend/54.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/ai-selfie-video-trend/55.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/ai-selfie-video-trend/56.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/ai-selfie-video-trend/57.avif',
    ],
    featuresList: [
      'Selfie upload with URL or file input',
      'Movie scene selection with preview cards',
      'AI-powered video generation pipeline',
      'Real-time processing status & progress bar',
      'Downloadable & shareable video output',
      'Modern, mobile-friendly UI',
    ],
    templateKey: PTK.AISELFIEVIDEO,
  },
  {
    id: '6',
    titleKey: 'chat.examplePrompts.prompt6.title',
    textKey: 'chat.examplePrompts.prompt6.text',
    titleDefault: 'Yellow Studio',
    textDefault:
      'A premium digital agency website with a modern, sleek design, smooth transitions, and a focus on high-end visual storytelling for creative studios.',
    icon: 'landingPageIcon',
    image: 'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/new-templates/thumbnails/yellow.avif',
    repoUrl: 'https://github.com/dualiteindia/Yellow-Studio-Agency',
    framework: 'react_tailwind',
    category: 'website',
    aboutTemplate:
      'Yellow Studio is a bold, creative agency website template with eye-catching yellow accents, smooth page transitions, and a portfolio-first design. Perfect for design agencies, freelancers, and creative studios wanting to showcase their best work.',
    isPaid: false,
    price: 0,
    previewLink: 'https://yellow-studio.dualite.app/',
    screenshotLinks: [
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/yellow-studio/82.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/yellow-studio/10.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/yellow-studio/11.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/yellow-studio/12.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/yellow-studio/9.avif',
    ],
    featuresList: [
      'Bold hero section with dynamic typography',
      'Portfolio grid with hover effects & case studies',
      'Services section with animated icons',
      'Client logo carousel & testimonials',
      'Contact form with validation',
      'Smooth page transitions & scroll animations',
    ],
    templateKey: PTK.YELLOWSTUDIO,
  },
  {
    id: '7',
    titleKey: 'chat.examplePrompts.prompt7.title',
    textKey: 'chat.examplePrompts.prompt7.text',
    titleDefault: 'Jane AI Agency Website',
    textDefault: 'Create a high-converting website for an AI agency',
    icon: 'landingPageIcon',
    image: 'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/new-templates/thumbnails/jane.avif',
    repoUrl: 'https://github.com/dualiteindia/jane-ai-agency',
    framework: 'react_tailwind',
    category: 'website',
    aboutTemplate:
      'Jane AI is a high-converting landing page template for AI agencies and tech startups. Features a futuristic design with glassmorphism effects, animated stats counters, and a clear service breakdown to help you win more clients.',
    isPaid: false,
    price: 0,
    previewLink: 'https://jane-ai.dualite.app/',
    screenshotLinks: [
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/jane-ai/83.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/jane-ai/17.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/jane-ai/18.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/jane-ai/19.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/jane-ai/20.avif',
    ],
    featuresList: [
      'Futuristic hero with glassmorphism effects',
      'Animated statistics & achievement counters',
      'Service breakdown with visual icons',
      'Case study showcase section',
      'Lead capture form with CTA',
      'Optimized for conversions & SEO',
    ],
    templateKey: PTK.JANEAIAGENCY,
  },
  {
    id: '8',
    titleKey: 'chat.examplePrompts.prompt8.title',
    textKey: 'chat.examplePrompts.prompt8.text',
    titleDefault: 'Norden',
    textDefault:
      'Norden is a furniture and interior design brand that specializes in creating beautiful and functional furniture for the home and office',
    icon: 'mobileAppIcon',
    image: 'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/new-templates/thumbnails/Norden.avif',
    repoUrl: 'https://github.com/dualiteindia/furniture-template',
    framework: 'react_tailwind',
    category: 'website',
    aboutTemplate:
      'Norden is a minimalist furniture brand website template with clean lines, elegant product showcases, and a sophisticated browsing experience. Ideal for Scandinavian-inspired brands, interior designers, and home décor businesses.',
    isPaid: false,
    price: 0,
    previewLink: 'https://norden.dualite.app/',
    screenshotLinks: [
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/norden/84.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/norden/21.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/norden/22.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/norden/23.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/norden/24.avif',
    ],
    featuresList: [
      'Minimalist Scandinavian-inspired design',
      'Product showcase with large imagery',
      'About section with brand story',
      'Newsletter signup integration',
      'Clean typography & whitespace design',
      'Fully responsive across all breakpoints',
    ],
    templateKey: PTK.NORDEN,
  },
  {
    id: '9',
    titleKey: 'chat.examplePrompts.prompt9.title',
    textKey: 'chat.examplePrompts.prompt9.text',
    titleDefault: '1 Reserve',
    textDefault: 'The 1Reserve is a multi-tenant ecosystem built for the modern visionaries',
    icon: 'mobileAppIcon',
    image: 'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/new-templates/thumbnails/hotel.avif',
    repoUrl: 'https://github.com/dualiteindia/hotels-template',
    framework: 'react_tailwind',
    category: 'website',
    aboutTemplate:
      '1 Reserve is a luxury hotel booking website template featuring an elegant design, room galleries, amenities showcase, and a multi-step reservation system. Built for boutique hotels, resorts, and hospitality brands.',
    isPaid: false,
    price: 0,
    previewLink: 'https://1reserve.dualite.app/',
    screenshotLinks: [
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/one-reserve/85.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/one-reserve/25.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/one-reserve/26.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/one-reserve/27.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/one-reserve/28.avif',
    ],
    featuresList: [
      'Luxury hero with full-screen imagery',
      'Room showcase with pricing & details',
      'Amenities & facilities section',
      'Multi-step booking / reservation flow',
      'Guest reviews & ratings display',
      'Gold-accented premium color scheme',
    ],
    templateKey: PTK.ONERESERVE,
  },
  {
    id: '10',
    titleKey: 'chat.examplePrompts.prompt10.title',
    textKey: 'chat.examplePrompts.prompt10.text',
    titleDefault: 'Jenny Hu Portfolio',
    textDefault: 'Prouduct designer portfolio website',
    icon: 'mobileAppIcon',
    image: 'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/new-templates/thumbnails/Jenny_Hu.avif',
    repoUrl: 'https://github.com/dualiteindia/jenny-portfolio-template',
    framework: 'react_tailwind',
    category: 'website',
    aboutTemplate:
      'Jenny Hu is a sleek product designer portfolio template with project showcases, hover interactions, and an about section. Perfect for designers, developers, and creatives who want to display their work with style.',
    isPaid: false,
    price: 0,
    previewLink: 'https://jenny.dualite.app/',
    screenshotLinks: [
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/jenny-hu-portfolio/86.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/jenny-hu-portfolio/29.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/jenny-hu-portfolio/30.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/jenny-hu-portfolio/31.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/jenny-hu-portfolio/32.avif',
    ],
    featuresList: [
      'Clean portfolio layout with project grid',
      'Hover interactions on project cards',
      'Detailed case study pages',
      'About section with skills & bio',
      'Contact form or email CTA',
      'Minimal, elegant typography',
    ],
    templateKey: PTK.JENNYHUPORTFOLIO,
  },
  {
    id: '11',
    titleKey: 'chat.examplePrompts.prompt11.title',
    textKey: 'chat.examplePrompts.prompt11.text',
    titleDefault: 'Batman Portfolio',
    textDefault: 'Batman themed design portfolio with hover interactions.',
    icon: 'landingPageIcon',
    image: 'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/new-templates/thumbnails/bruce.avif',
    repoUrl: 'https://github.com/dualiteindia/Design-Portfolio-Batman-theme',
    framework: 'react_tailwind',
    category: 'website',
    aboutTemplate:
      'Batman Portfolio is a dark-themed, cinematic portfolio template inspired by The Dark Knight aesthetic. Features dramatic hover effects, gritty textures, and a bold layout — ideal for creatives who want a unique, character-driven portfolio.',
    isPaid: false,
    price: 0,
    previewLink: 'https://portfolio.dualite.app/',
    screenshotLinks: [
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/batman/87.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/batman/33.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/batman/34.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/batman/35.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/batman/36.avif',
    ],

    featuresList: [
      'Cinematic dark theme with gritty textures',
      'Dramatic hover & reveal animations',
      'Project showcase with bold imagery',
      'Skills & experience section',
      'Batman-inspired color palette & typography',
      'Fully responsive hero with scroll effects',
    ],
    templateKey: PTK.BATMANPORTFOLIO,
  },
  {
    id: '12',
    titleKey: 'chat.examplePrompts.prompt12.title',
    textKey: 'chat.examplePrompts.prompt12.text',
    titleDefault: 'Claymist Luxury Interior Brand Template',
    textDefault:
      'Create a premium, earthy interior finishes website template inspired by natural clay and lime textures. Include calm layouts, soft editorial imagery, and eco-friendly storytelling throughout the pages',
    icon: 'landingPageIcon',
    image: 'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/new-templates/thumbnails/claymist.avif',
    repoUrl: 'https://github.com/dualiteindia/earthy-interior-template',
    framework: 'react_tailwind',
    category: 'website',
    aboutTemplate:
      'Claymist is a luxury interior brand template with earthy tones, editorial imagery, and eco-friendly storytelling. Designed for premium interior finishes, clay & lime texture brands, and sustainable home décor companies.',
    isPaid: false,
    price: 0,
    previewLink: 'https://claymist.dualite.app/',
    screenshotLinks: [
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/claymist/88.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/claymist/37.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/claymist/38.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/claymist/39.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/claymist/40.avif',
    ],
    featuresList: [
      'Editorial-style hero with organic imagery',
      'Product collection with texture previews',
      'Brand story & sustainability section',
      'Image gallery with earthy color grading',
      'Newsletter & contact integration',
      'Calm, eco-friendly design language',
    ],
    templateKey: PTK.CLAYMIST,
  },
  {
    id: '13',
    titleKey: 'chat.examplePrompts.prompt13.title',
    textKey: 'chat.examplePrompts.prompt13.text',
    titleDefault: 'FigJam Style Flowchart Builder',
    textDefault:
      'Build a fully interactive frontend clone of FigJam which are used for building flowcharts and user-flow diagrams. Include a large central white canvas where users can draw and select shapes with full mouse support. Allow creation and manipulation of shapes: rectangle, ellipse, triangle, arrow, line, and freehand pencil. Include a text tool that lets users type directly on the canvas. Users should be able to select, move, resize, and rotate elements using drag handles. Support undo/redo, zoom in/out, and panning via mouse or touch gestures. Add a top toolbar with all tools (select, pencil, line, arrow, rectangle, ellipse, text, eraser), a color picker, stroke width selector, and export (PNG/SVG). Use a left sidebar for layers or object properties. Ensure smooth performance with canvas interactions, like holding shift to lock aspect ratio while resizing. Use a modern, minimal UI style with floating panels and clean icons. Do not use any backend. Everything should be in React with HTML canvas or SVG. Prioritize interactivity, real-time rendering, and precise positioning. Make the layout responsive and mobile-friendly. Keep behavior as close to the real FigJam product as possible.',
    icon: 'flowChartIcon',
    image: 'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/new-templates/thumbnails/figjam.avif',
    repoUrl: 'https://github.com/dualiteindia/flowchart-builder-example-prompt',
    framework: 'react_tailwind',
    category: 'interactive',
    aboutTemplate:
      'FigJam Flowchart Builder is a fully interactive diagramming tool built entirely in the browser. Create flowcharts, user flows, and diagrams with drag-and-drop shapes, freehand drawing, zoom/pan controls, and export capabilities — no backend required.',
    isPaid: false,
    price: 0,
    previewLink: 'https://flowchart-builder.dualite.app/',
    screenshotLinks: [
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/figJam-style-flowchart-builder/41.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/figJam-style-flowchart-builder/41.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/figJam-style-flowchart-builder/42.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/figJam-style-flowchart-builder/43.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/figJam-style-flowchart-builder/44.avif',
    ],
    featuresList: [
      'Interactive canvas with shapes & freehand drawing',
      'Drag, resize, rotate elements with handles',
      'Undo/Redo & zoom/pan controls',
      'Top toolbar with all drawing tools',
      'Color picker & stroke width customization',
      'Export to PNG/SVG formats',
    ],
    templateKey: PTK.FIGJAMFLOWCHART,
  },
  {
    id: '14',
    titleKey: 'chat.examplePrompts.prompt14.title',
    textKey: 'chat.examplePrompts.prompt14.text',
    titleDefault: 'Investify App',
    textDefault:
      'A GenZ-first investing app that makes wealth-building simple and social by offering smart portfolios, bite-sized insights, and goal-based investing for the next generation.',
    icon: 'mobileAppIcon',
    image: 'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/new-templates/thumbnails/Investify.avif',
    repoUrl: 'https://github.com/dualiteindia/Investify',
    framework: 'react_tailwind',
    category: 'mobile',
    aboutTemplate:
      'Investify is a GenZ-first investing app template with vibrant gradients, social investing features, and bite-sized financial insights. Includes portfolio dashboards, goal tracking, and a modern mobile-first UI perfect for fintech startups.',
    isPaid: false,
    price: 0,
    previewLink: '',
    screenshotLinks: [
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/investify-app/115.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/investify-app/111.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/investify-app/112.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/investify-app/113.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/investify-app/114.avif',
    ],
    featuresList: [
      'Vibrant mobile-first portfolio dashboard',
      'Smart portfolio recommendations',
      'Goal-based investing tracker',
      'Bite-sized financial insights & tips',
      'Social investing features & leaderboards',
      'Modern GenZ-friendly design language',
    ],
    templateKey: PTK.INVESTIFY,
  },
  {
    id: '15',
    titleKey: 'chat.examplePrompts.prompt15.title',
    textKey: 'chat.examplePrompts.prompt15.text',
    titleDefault: 'AI Fashion Studio',
    textDefault: 'Put any piece of your cloth and turn into professional studio-looking photoshoot',
    icon: 'mobileAppIcon',
    image: 'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/new-templates/thumbnails/ai-fashion.avif',
    repoUrl: 'https://github.com/dualiteindia/ai-fashion-studio',
    framework: 'react_tailwind',
    category: 'ai',
    aboutTemplate:
      'AI Fashion Studio transforms your clothing photos into professional studio-quality photoshoots using AI. Upload any piece of clothing and get polished, editorial-style images — perfect for e-commerce brands, fashion designers, and content creators.',
    isPaid: false,
    price: 0,
    previewLink: 'https://fashion-studio.dualite.app/',
    screenshotLinks: [
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/ai-fashion-studio/63.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/ai-fashion-studio/59.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/ai-fashion-studio/60.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/ai-fashion-studio/61.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/ai-fashion-studio/62.avif',
    ],
    featuresList: [
      'Drag & drop clothing image upload',
      'AI-powered studio photoshoot generation',
      'Multiple style & background options',
      'High-resolution output downloads',
      'Before/after comparison slider',
      'Gallery of generated photoshoots',
    ],
    templateKey: PTK.AIFASHIONSTUDIO,
  },
  {
    id: '16',
    titleKey: 'chat.examplePrompts.prompt16.title',
    textKey: 'chat.examplePrompts.prompt16.text',
    titleDefault: "Don't Die Single - Powered by Higgsfield and Gemini",
    textDefault: 'Tinder with AI where you can chat and interact with AI avatars through video as well as text.',
    icon: 'mobileAppIcon',
    image: 'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/new-templates/thumbnails/ai-dating.avif',
    repoUrl: 'https://github.com/dualiteindia/ai-dating-app',
    framework: 'react_tailwind',
    category: 'ai',
    aboutTemplate:
      "Don't Die Single is an AI-powered dating app template featuring swipe-based matching with AI avatars, text & video chat, and personality-driven matchmaking. Powered by Higgsfield and Gemini for realistic AI interactions.",
    isPaid: false,
    price: 0,
    previewLink: 'https://dont-die-single.dualite.app/',
    screenshotLinks: [
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/dont-die-single/68.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/dont-die-single/64.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/dont-die-single/65.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/dont-die-single/66.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/dont-die-single/67.avif',
    ],
    featuresList: [
      'Swipe-based matching interface',
      'AI avatar text & video chat',
      'Personality-driven matchmaking algorithm',
      'Profile creation & customization',
      'Powered by Higgsfield & Gemini AI',
      'Modern mobile-first dating UI',
    ],
    templateKey: PTK.DONTDIESINGLE,
  },
  {
    id: '17',
    titleKey: 'chat.examplePrompts.prompt17.title',
    textKey: 'chat.examplePrompts.prompt17.text',
    titleDefault: 'Playful and fun typewriter',
    textDefault: 'A nostalgic and interactive typewriter experience with playful sounds and animations.',
    icon: 'landingPageIcon',
    image: 'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/new-templates/thumbnails/Typewriter.avif',
    repoUrl: 'https://github.com/dualiteindia/playful-typewriter',
    framework: 'react_tailwind',
    category: 'interactive',
    aboutTemplate:
      'Playful Typewriter is a nostalgic, interactive typing experience with realistic key sounds, ink effects, and paper scroll animations. A delightful creative tool for writers, storytellers, or anyone who loves the charm of vintage typewriters.',
    isPaid: false,
    price: 0,
    previewLink: 'https://typewriter.dualite.app/',
    screenshotLinks: [
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/playful-and-fun-typewriter/100.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/playful-and-fun-typewriter/96.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/playful-and-fun-typewriter/97.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/playful-and-fun-typewriter/98.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/playful-and-fun-typewriter/99.avif',
    ],
    featuresList: [
      'Realistic typewriter key press sounds',
      'Ink splatter & paper scroll animations',
      'Vintage paper texture background',
      'Character-by-character typing effect',
      'Font & speed customization options',
      'Export typed content as text or image',
    ],
    templateKey: PTK.TYPEWRITER,
  },
  {
    id: '18',
    titleKey: 'chat.examplePrompts.prompt18.title',
    textKey: 'chat.examplePrompts.prompt18.text',
    titleDefault: 'Memory Lane: (Image relive)',
    textDefault: 'relive your old image and bring them into life using AI.',
    icon: 'mobileAppIcon',
    image: 'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/new-templates/thumbnails/memory.avif',
    repoUrl: 'https://github.com/dualiteindia/AI-Image-Relive',
    framework: 'react_tailwind',
    category: 'ai',
    aboutTemplate:
      'Memory Lane brings your old photos back to life using AI. Upload vintage or faded images, and the AI restores, colorizes, and animates them — creating beautiful, shareable memories from the past.',
    isPaid: false,
    price: 0,
    previewLink: 'https://image-relive.dualite.app/',
    screenshotLinks: [
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/memory-lane/105.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/memory-lane/101.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/memory-lane/102.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/memory-lane/103.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/memory-lane/104.avif',
    ],
    featuresList: [
      'Old photo upload with drag & drop',
      'AI-powered image restoration & colorization',
      'Photo animation & enhancement effects',
      'Before/after comparison viewer',
      'Share & download restored memories',
      'Gallery of restored images',
    ],
    templateKey: PTK.MEMORYLANE,
  },
  {
    id: '19',
    titleKey: 'chat.examplePrompts.prompt19.title',
    textKey: 'chat.examplePrompts.prompt19.text',
    titleDefault: 'Super Mario (Xmas edition)',
    textDefault: 'super mario in christmas edition',
    icon: 'landingPageIcon',
    image: 'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/new-templates/thumbnails/Mario.webp',
    repoUrl: 'https://github.com/dualiteindia/mario-xmas-edition',
    framework: 'react_tailwind',
    category: 'interactive',
    aboutTemplate:
      'Super Mario Xmas Edition is a festive browser-based platformer game built with React. Features Christmas-themed levels, holiday power-ups, and classic Mario gameplay mechanics — all running in the browser with no plugins required.',
    isPaid: false,
    price: 0,
    previewLink: 'https://super-mario.dualite.app/',
    screenshotLinks: [
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/super-mario-xmas-edition/110.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/super-mario-xmas-edition/106.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/super-mario-xmas-edition/107.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/super-mario-xmas-edition/108.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/super-mario-xmas-edition/109.avif',
    ],
    featuresList: [
      'Browser-based platformer gameplay',
      'Christmas-themed levels & power-ups',
      'Classic Mario movement & physics',
      'Score tracking & leaderboard',
      'Keyboard & touch controls support',
      'Festive sound effects & music',
    ],
    templateKey: PTK.SUPERMARIO,
  },
  {
    id: '20',
    titleKey: 'chat.examplePrompts.prompt20.title',
    textKey: 'chat.examplePrompts.prompt20.text',
    titleDefault: 'Van Gogh styler(powered by Higgsfield and NanoBanana Pro )',
    textDefault: 'convert any image into van gogh style',
    icon: 'mobileAppIcon',
    image: 'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/new-templates/thumbnails/van.webp',
    repoUrl: 'https://github.com/dualiteindia/Van-Gogh-styler',
    framework: 'react_tailwind',
    category: 'ai',
    aboutTemplate:
      'Van Gogh Styler transforms any image into stunning Van Gogh-inspired artwork using AI. Powered by Higgsfield and NanoBanana Pro, it applies iconic brushstroke patterns and color palettes to your photos — perfect for art lovers and creative projects.',
    isPaid: false,
    price: 0,
    previewLink: 'https://van-gogh.dualite.app/',
    screenshotLinks: [
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/van-gogh-styler/120.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/van-gogh-styler/116.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/van-gogh-styler/117.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/van-gogh-styler/118.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/van-gogh-styler/119.avif',
    ],
    featuresList: [
      'Image upload with instant preview',
      'AI-powered Van Gogh style transfer',
      'Multiple artistic style variations',
      'Powered by Higgsfield & NanoBanana Pro',
      'High-resolution output downloads',
      'Side-by-side comparison view',
    ],
    templateKey: PTK.VANGOGHSTYLER,
  },
  {
    id: '21',
    titleKey: 'chat.examplePrompts.prompt21.title',
    textKey: 'chat.examplePrompts.prompt21.text',
    titleDefault: 'AI Voice Receptionist (powered by Voxtropy)',
    textDefault:
      'AI voice receptionist for a San Francisco dentist clinic that delivers accurate, always-available, patient-friendly appointment and care coordination.',
    icon: 'mobileAppIcon',
    image: 'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/new-templates/thumbnails/Receptioninst.webp',
    repoUrl: 'https://github.com/dualiteindia/ai-voice-receptionist',
    framework: 'react_tailwind',
    category: 'ai',
    aboutTemplate:
      'AI Voice Receptionist is a smart, always-available voice assistant for businesses. Built for a dentist clinic use-case, it handles appointment scheduling, patient queries, and care coordination — all through natural AI-powered voice conversations.',
    isPaid: false,
    price: 0,
    previewLink: 'https://voice-receptionist.dualite.app/',
    screenshotLinks: [
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/ai-voice-receptionist/73.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/ai-voice-receptionist/69.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/ai-voice-receptionist/70.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/ai-voice-receptionist/71.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/ai-voice-receptionist/72.avif',
    ],
    featuresList: [
      'AI-powered voice conversation interface',
      'Appointment scheduling & management',
      'Patient query handling & FAQ support',
      'Powered by Voxtropy voice AI',
      'Dashboard for conversation analytics',
      'Customizable voice & response settings',
    ],
    templateKey: PTK.AIVOICERECEPTIONIST,
  },
  {
    id: '22',
    titleKey: 'chat.examplePrompts.prompt22.title',
    textKey: 'chat.examplePrompts.prompt22.text',
    titleDefault: 'HR Dashboard',
    textDefault: 'Comprehensive HR & Workforce Management Dashboard designed for an administrator or manager.',
    icon: 'landingPageIcon',
    image: 'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/new-templates/thumbnails/HRDash.avif',
    repoUrl: 'https://github.com/dualiteindia/hr-dashboard-template',
    framework: 'react_tailwind',
    category: 'dashboard',
    aboutTemplate:
      'HR Dashboard is a comprehensive workforce management template with employee tracking, attendance management, performance reviews, and org chart visualization. Designed for HR administrators and people managers in modern organizations.',
    isPaid: false,
    price: 0,
    previewLink: 'https://hr-dash.dualite.app/',
    screenshotLinks: ['https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/new-templates/thumbnails/HRDash.avif'],
    featuresList: [
      'Employee overview with key metrics',
      'Attendance tracking & leave management',
      'Performance review dashboards',
      'Organizational chart visualization',
      'Data tables with sorting & filtering',
      'Charts & analytics with real-time data',
    ],
    templateKey: PTK.HRDASHBOARD,
  },
  {
    id: '23',
    titleKey: 'chat.examplePrompts.prompt23.title',
    textKey: 'chat.examplePrompts.prompt23.text',
    titleDefault: 'Pomodoro Todo App',
    textDefault:
      'A Pomodoro-powered to-do app that grows a virtual tree with every focused session, turning productivity into a visible habit.',
    icon: 'landingPageIcon',
    image: 'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/new-templates/thumbnails/pomodoro.avif',
    repoUrl: 'https://github.com/dualiteindia/pomodoro-template',
    framework: 'react_tailwind',
    category: 'website',
    aboutTemplate:
      'Pomodoro Todo App combines the Pomodoro technique with task management and gamification. Complete focused work sessions to grow a virtual tree — making productivity visible and rewarding. Perfect for students, freelancers, and anyone building better work habits.',
    isPaid: false,
    price: 0,
    previewLink: 'https://pomo-timer.dualite.app/',
    screenshotLinks: ['https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/new-templates/thumbnails/pomodoro.avif'],
    featuresList: [
      'Pomodoro timer with work/break cycles',
      'Task management with priority levels',
      'Virtual tree growth with completed sessions',
      'Productivity statistics & streaks',
      'Customizable timer durations',
      'Ambient sounds for focused sessions',
    ],
    templateKey: PTK.POMODOROTODO,
  },
  {
    id: '24',
    titleKey: 'chat.examplePrompts.prompt24.title',
    textKey: 'chat.examplePrompts.prompt24.text',
    titleDefault: 'Sneakers Ecommerce Website',
    textDefault:
      'A premium, sleek sneakers ecommerce website with a focus on high-end visual storytelling for sneaker brands.',
    icon: 'landingPageIcon',
    image: 'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/new-templates/thumbnails/sneakers.avif',
    repoUrl: 'https://github.com/dualiteindia/sneakers-template',
    framework: 'react_tailwind',
    category: 'website',
    aboutTemplate:
      'Sneakers Ecommerce is a premium online store template for sneaker brands with 3D product displays, size selection, wishlist functionality, and a sleek checkout flow. Built for streetwear brands and sneaker retailers.',
    isPaid: false,
    price: 0,
    previewLink: 'https://sneakers.dualite.app/',
    screenshotLinks: [
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/sneaker-site/95.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/sneaker-site/91.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/sneaker-site/92.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/sneaker-site/93.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/sneaker-site/94.avif',
    ],
    featuresList: [
      'Premium product cards with hover effects',
      'Size selection & color variants',
      'Wishlist & favorites functionality',
      'Shopping cart with quantity controls',
      'Sleek checkout flow design',
      'Brand storytelling sections',
    ],
    templateKey: PTK.SNEAKERSECOMMERCE,
  },
  {
    id: '25 ',
    titleKey: 'chat.examplePrompts.prompt25.title',
    textKey: 'chat.examplePrompts.prompt25.text',
    titleDefault: 'Sketch Enhance',
    textDefault:
      'SketchEnhance is an AI-powered sketch-to-anime generator. Draw a quick doodle, upload it, and instantly transform your idea into a polished anime-style illustration.',
    icon: 'landingPageIcon',
    image: 'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/new-templates/thumbnails/doodle.avif',
    repoUrl: 'https://github.com/dualiteindia/doodle-to-anime',
    framework: 'react_tailwind',
    category: 'ai',
    aboutTemplate:
      'Sketch Enhance transforms rough doodles into polished anime-style illustrations using AI. Draw or upload a sketch, and the AI generates a professional-quality anime illustration — perfect for artists, content creators, and anime enthusiasts.',
    isPaid: false,
    price: 0,
    previewLink: 'https://sketch-enhance.dualite.app/',
    screenshotLinks: ['https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/new-templates/thumbnails/doodle.avif'],
    featuresList: [
      'In-browser drawing canvas for sketches',
      'Upload existing doodles or drawings',
      'AI-powered anime style transformation',
      'Multiple anime art style options',
      'High-res download of generated art',
      'Gallery of community transformations',
    ],
    templateKey: PTK.SKETCHENHANCE,
  },
  {
    id: '26',
    titleKey: 'chat.examplePrompts.prompt26.title',
    textKey: 'chat.examplePrompts.prompt26.text',
    titleDefault: 'Munchr - Food Delivery App',
    textDefault:
      'A feature-rich food delivery mobile application targeting the US market. Built with React Native, it offers a seamless ordering experience with real-time tracking and diverse cuisine options.',
    icon: 'mobileAppIcon',
    image: 'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/new-templates/thumbnails/munchr.avif',
    repoUrl: 'https://github.com/dualiteindia/food-delivery-app-munchr',
    framework: 'reactnative',
    category: 'mobile',
    aboutTemplate:
      'Munchr is a feature-rich food delivery app template built with React Native. Includes restaurant browsing, menu exploration, cart management, real-time order tracking, and a polished mobile experience targeting the US market.',
    isPaid: false,
    price: 0,
    previewLink: '',
    screenshotLinks: ['https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/new-templates/thumbnails/munchr.avif'],
    featuresList: [
      'Restaurant browsing with cuisine filters',
      'Detailed menu with customization options',
      'Cart management & order summary',
      'Real-time delivery tracking with map',
      'User profile & order history',
      'Built with React Native for iOS & Android',
    ],
    templateKey: PTK.MUNCHR,
  },
  {
    id: '28',
    titleKey: 'chat.examplePrompts.prompt28.title',
    textKey: 'chat.examplePrompts.prompt28.text',
    titleDefault: 'NovaPay Digital Banking Dashboard',
    textDefault:
      'A modern digital banking dashboard that lets users track balances, monitor transactions, and manage investments in one unified interface.',
    icon: 'landingPageIcon',
    image: 'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/banking-dashboard/template.avif',
    repoUrl: 'https://github.com/dualiteindia/nova-pay',
    framework: 'react_tailwind',
    category: 'dashboard',
    aboutTemplate:
      'NovaPay is a modern digital banking dashboard template with balance tracking, transaction monitoring, investment management, and spending analytics. Designed for fintech startups and digital banking products with a clean, professional UI.',
    isPaid: false,
    price: 0,
    previewLink: 'https://nova.dualite.app/',
    screenshotLinks: [
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/banking-dashboard/126.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/banking-dashboard/127.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/banking-dashboard/128.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/banking-dashboard/129.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/banking-dashboard/130.avif',
    ],
    featuresList: [
      'Account balance overview with cards',
      'Transaction history with search & filters',
      'Investment portfolio tracker',
      'Spending analytics with charts',
      'Transfer & payment interfaces',
      'Clean, professional banking UI design',
    ],
    templateKey: PTK.NOVAPAY,
  },
  {
    id: '29',
    titleKey: 'chat.examplePrompts.prompt29.title',
    textKey: 'chat.examplePrompts.prompt29.text',
    titleDefault: 'Cleer Finance App',
    textDefault:
      'A modern corporate spend management application that helps businesses track expenses, manage budgets, and handle company cards.',
    icon: 'mobileAppIcon',
    image: 'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/new-templates/thumbnails/cleer.avif',
    repoUrl: 'https://github.com/dualiteindia/cleer-finance-template',
    framework: 'reactnative',
    category: 'mobile',
    aboutTemplate:
      'Cleer Finance is a modern, all-in-one corporate spend management and financial tracking platform designed to simplify company expenses. By combining virtual corporate cards, automated receipt capture, budget tracking, and integrated travel bookings, it streamlines financial operations for both employees and finance teams.',
    isPaid: false,
    price: 0,
    previewLink: '',
    screenshotLinks: [
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/cleer-finance/1-3.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/cleer-finance/2-3.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/cleer-finance/3-3.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/cleer-finance/4-3.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/cleer-finance/5-3.avif',
    ],
    featuresList: [
      'Virtual & physical corporate card management',
      'Real-time balance tracking & spend limits',
      'Automated receipt capture & expense reporting',
      'Integrated travel booking & trip budgets',
      'Categorized budget management system',
      'Seamless employee fund requests & insights',
    ],
    templateKey: PTK.CLEERFINANCE,
  },
  {
    id: '30',
    titleKey: 'chat.examplePrompts.prompt30.title',
    textKey: 'chat.examplePrompts.prompt30.text',
    titleDefault: 'Straton AI',
    textDefault: 'Production-ready AI Solutions for BFSI. Not just prototypes.',
    icon: 'mobileAppIcon',
    image: 'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/new-templates/thumbnails/straton.avif',
    repoUrl: 'https://github.com/dualiteindia/straton-template',
    framework: 'react_tailwind',
    category: 'website',
    aboutTemplate: 'Production-ready AI Solutions for BFSI. Not just prototypes.',
    isPaid: false,
    price: 0,
    previewLink: 'https://straton.dualite.app/',
    screenshotLinks: [
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/stranton/1-1.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/stranton/2-1.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/stranton/3-1.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/stranton/4-1.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/stranton/5-1.avif',
    ],
    featuresList: [
      'Enterprise-focused hero section with high-trust design',
      'Core AI capabilities breakdown for Banking & Insurance',
      'Data privacy & regulatory compliance showcases',
      'Interactive ROI calculators & performance metrics',
      'Secure consultation booking & lead capture forms',
    ],
    templateKey: PTK.STRATONAI,
  },
  {
    id: '31',
    titleKey: 'chat.examplePrompts.prompt31.title',
    textKey: 'chat.examplePrompts.prompt31.text',
    titleDefault: 'Ignite Coffee Shop',
    textDefault: 'Clean energy coffee built for creators and athletes.',
    icon: 'landingPageIcon',
    image: 'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/new-templates/thumbnails/ignite-coffee.avif',
    repoUrl: 'https://github.com/dualiteindia/ignite-coffee-template',
    framework: 'react_tailwind',
    category: 'website',
    aboutTemplate: 'Clean energy coffee built for creators and athletes.',
    isPaid: false,
    price: 0,
    previewLink: 'https://ignite-coffee.dualite.app/',
    screenshotLinks: [
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/ignite-coffee/1-2.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/ignite-coffee/2-2.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/ignite-coffee/3-2.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/ignite-coffee/4-2.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/ignite-coffee/5-2.avif',
    ],
    featuresList: [
      'High-energy, visually striking hero section',
      'DTC coffee product listings & subscriptions',
      'Clean energy ingredients & nutritional breakdown',
      'Lifestyle imagery for athletes & creators',
      'Smooth checkout and cart management workflow',
    ],
    templateKey: PTK.IGNITECOFFEE,
  },
  {
    id: '32',
    titleKey: 'chat.examplePrompts.prompt32.title',
    textKey: 'chat.examplePrompts.prompt32.text',
    titleDefault: 'Lorvique',
    textDefault: 'Masters of Light Since 1895. Discover luxury jewelry, watches, and accessories.',
    icon: 'landingPageIcon',
    image: 'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/new-templates/thumbnails/lorvique.avif',
    repoUrl: 'https://github.com/dualiteindia/lorvique-template',
    framework: 'react_tailwind',
    category: 'website',
    aboutTemplate: 'Masters of Light Since 1895. Discover luxury jewelry, watches, and accessories.',
    isPaid: false,
    price: 0,
    previewLink: 'https://lorvique-template.dualite.app/',
    screenshotLinks: [
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/lorvique/5.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/lorvique/1.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/lorvique/2.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/lorvique/3.avif',
      'https://69sfgmk1pv2omedb.public.blob.vercel-storage.com/alpha-templates/lorvique/4.avif',
    ],
    featuresList: [
      'Elegant, luxury-focused hero section',
      'Premium product showcase for jewelry & watches',
      'Heritage and brand story section',
    ],
    templateKey: PTK.LORVIQUE,
  },
];

export const CATEGORIES = [
  { id: 'all', label: 'All', icon: 'i-ph:squares-four-bold' },
  { id: 'website', label: 'Websites', icon: 'i-ph:browser-bold' },
  { id: 'ai', label: 'AI Apps', icon: 'i-ph:cpu-bold' },
  { id: 'interactive', label: 'Interactive Apps', icon: 'i-ph:briefcase-bold' },
  { id: 'dashboard', label: 'Dashboard', icon: 'i-ph:user-bold' },
  { id: 'mobile', label: 'Mobile Apps', icon: 'i-ph:device-mobile-bold' },
];
